import { RefObject, useRef, useState } from 'react'

import { Box, Button, Form, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  AtsConversationMessagesDocument,
  AtsCreateMessageDocument,
  AtsCreateMessageMutation,
  AtsMessageInboxConversationsDocument,
} from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import { type EditorHandle } from 'modules/core/components/TextEditor'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import app from 'modules/core/store/models/app'

import { SendMessageButton } from 'modules/ats/components/SendMessageButton'
import { TextEditorWithMessageTemplates } from 'modules/ats/components/TextEditorWithMessageTemplates'
import { CreateMessageTemplateModal } from 'modules/ats/components/message_templates/CreateMessageTemplateModal'

import { ConfirmationModal } from './ConfirmationModal'

interface MessageCreateFormValues {
  attachments: File[]
  content: string
  delayedUntil?: string | null
  sendAsCorporateEmail: boolean
}

interface Props {
  applicationId: string
  candidateFullName: string
  scrollRef: RefObject<HTMLElement>
  hasMessageTemplatesFeature: boolean
}

export const NewMessage = ({
  applicationId,
  candidateFullName,
  scrollRef,
  hasMessageTemplatesFeature,
}: Props) => {
  const editorRef = useRef<EditorHandle>(null)
  const richTextEditorRef = useRef<RichTextEditorHandle>(null)
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [showCreateMessageTemplateModal, toggleCreateMessageTemplateModal] = useToggle(false)
  const [rememberConfirmation, setRememberConfirmation] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.atsNewMessageRememberConfirmation
  )

  const [createAtsMessage, { loading: creating }] = useMutation(AtsCreateMessageDocument, {
    extractorFn: (data: AtsCreateMessageMutation) => data.atsMutations.createAtsMessage,
    refetchQueries: [AtsMessageInboxConversationsDocument, AtsConversationMessagesDocument],
  })
  const messageDraftStorageKey = `${LOCAL_STORAGE_KEYS.atsNewMessageDraft}:${applicationId}`
  const [messageDraft, setMessageDraft] = useLocalStorage<string>(messageDraftStorageKey)
  const [rememberConfirmationCheckbox, setRememberConfirmationCheckbox] = useState(false)

  const {
    access: { userId: userId },
  } = useCurrentSession()

  const form = useForm<MessageCreateFormValues>({
    defaultValues: {
      attachments: [],
      content: messageDraft || '',
      sendAsCorporateEmail: false,
    },
    validate(values) {
      const formErrors: FormErrors<MessageCreateFormValues> = {}

      if (!values.content || values.content === '') {
        formErrors.content = i18n.t('common.empty')
      }

      return formErrors
    },
    onSubmit: async (data) => {
      const response = await createAtsMessage({
        variables: {
          ...data,
          atsApplicationId: applicationId,
          // Todo: cant use access current session as we need the user
          // new column access and migrate everything there
          sentById: userId,
          sentByType: 'user',
        },
      })

      const handled = handleResult(response, () => undefined)

      if (handled.success) {
        const message = data.delayedUntil
          ? i18n.t('ats.candidates.messages.new_message.success_delayed_message')
          : i18n.t('ats.candidates.messages.new_message.success_message')

        app.setFlash({
          message,
          type: 'correct',
        })

        afterSend()
      } else {
        const errorMessage =
          handled?.errors?.[0] && 'messages' in handled.errors[0] && handled.errors[0].messages?.[0]
            ? handled.errors[0].messages[0]
            : i18n.t('common.error')

        app.setFlash({
          message: errorMessage,
          type: 'wrong',
        })
      }

      return handled
    },
  })

  const onFilesChange = useUnsafeCallback((files: File[] | null | undefined) => {
    if (files && files?.length > 0) {
      form.setFieldValue('attachments', files)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const messageContent = form.values.content
  const isMessageEmpty = !messageContent || messageContent === ''

  const createDelayedMessage = async (delayedUntil: string) => {
    if (isMessageEmpty) return
    form.setFieldValue('delayedUntil', delayedUntil)
    form.submitForm()
  }

  const confirmOrCreateMessage = () => {
    if (creating || isMessageEmpty) return

    if (!rememberConfirmation) {
      return setIsConfirmationOpen(true)
    }

    form.submitForm()
  }

  const afterSend = () => {
    editorRef?.current?.clear()
    editorRef?.current?.clearFiles()
    richTextEditorRef?.current?.clear()
    richTextEditorRef?.current?.clearFiles()
    localStorage.removeItem(messageDraftStorageKey)
    form.resetForm()

    if (!rememberConfirmation) {
      setRememberConfirmation(rememberConfirmationCheckbox)
      setIsConfirmationOpen(false)
    }
  }

  const onChangeMessageContent = (value: string | null | undefined) => {
    form.setFieldValue('content', value)
    setMessageDraft(value || '')
  }

  return (
    <>
      <Form
        padding={null}
        onSubmit={() => {
          !hasMessageTemplatesFeature && confirmOrCreateMessage()
        }}
      >
        <Box gap="s16" ref={scrollRef}>
          <TextEditorWithMessageTemplates
            richTextEditorRef={richTextEditorRef}
            editorRef={editorRef}
            onFiles={onFilesChange}
            onChange={(content) => onChangeMessageContent(content)}
            placeholder={i18n.t('ats.candidates.messages.new_message.placeholder', {
              candidate: candidateFullName,
            })}
            value={form.values.content}
            showLabel={false}
          />

          {hasMessageTemplatesFeature ? (
            <Box flexDirection="row" gap="s8">
              <SendMessageButton
                label={i18n.t('ats.candidates.messages.new_message.send_now')}
                disabled={creating || isMessageEmpty}
                onSendMessage={confirmOrCreateMessage}
                onSendDelayedMessage={createDelayedMessage}
              />
              <Button
                onClick={toggleCreateMessageTemplateModal}
                disabled={isMessageEmpty}
                type="secondary"
              >
                {i18n.t('ats.manage_message_templates.save_as_template')}
              </Button>
              <TogglerCheckbox
                value={form.values.sendAsCorporateEmail}
                onChange={(sendAsCorporateEmail) =>
                  form.setFieldValue('sendAsCorporateEmail', sendAsCorporateEmail)
                }
                label={i18n.t('ats.candidates.messages.new_message.send_as_corporate')}
                information={i18n.t(
                  'ats.candidates.messages.new_message.send_as_corporate_tooltip'
                )}
              />
            </Box>
          ) : (
            <Form.Buttons>
              <Button disabled={creating || isMessageEmpty} submit>
                {i18n.t('ats.candidates.messages.new_message.send_email')}
              </Button>
            </Form.Buttons>
          )}
        </Box>
      </Form>

      {isConfirmationOpen && (
        <ConfirmationModal
          candidateFullName={candidateFullName}
          onClose={() => setIsConfirmationOpen(false)}
          onSubmit={(closePortal) => {
            form.submitForm()
            closePortal()
          }}
          onClick={() => {
            setRememberConfirmationCheckbox(!rememberConfirmationCheckbox)
          }}
          checked={rememberConfirmationCheckbox}
        />
      )}

      {showCreateMessageTemplateModal && (
        <CreateMessageTemplateModal
          content={messageContent}
          onClose={toggleCreateMessageTemplateModal}
        />
      )}
    </>
  )
}
